// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Button, { ButtonPresentationProps } from "@bg/MobiusOverrides/Button/Button";
import { ProductAvailabilityStyles, productAvailabilityStyles } from "@bg/Overrides/Components/ProductAvailability";
import ProductContextAvailability from "@bg/Overrides/Components/ProductContextAvailability";
import { ProductPriceStyles, productPriceStyles } from "@bg/Overrides/Components/ProductPrice";
import ProductPrice from "@bg/Overrides/Components/ProductPrice";
import { ProductQtyOrderedStyles } from "@bg/Overrides/Components/ProductQuantityOrdered";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getIsProductCompareFull,
    getProductCompareChecked,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showAddToList: boolean;
    showCompare?: boolean;
    showPrice?: boolean;
    showAvailability?: boolean;
}

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => ({
    productSettings: getSettingsCollection(state).productSettings,
    view: state.pages.productList.view || getSettingsCollection(state).productSettings.defaultViewType,
    productCompareChecked: getProductCompareChecked(state, props.productContext),
    isProductCompareFull: getIsProductCompareFull(state),
});

const mapDispatchToProps = { addProductToCompare, removeProductFromCompare };

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    // grid
    gridActionsWrapper?: InjectableCss;
    gridPriceAvailablityWrapper?: InjectableCss;
    gridPriceWrapper?: InjectableCss;
    productPriceWrapper?: InjectableCss;
    gridInventoryWrapper?: InjectableCss;
    gridActionsContentWrapper?: InjectableCss;

    // list
    listActionsWrapper?: InjectableCss;
    listPriceWrapper?: InjectableCss;
    listActionsContentWrapper?: InjectableCss;
    listCompareListWrapper?: InjectableCss;
    listAddToListWrapper?: InjectableCss;
    listCompareWrapper?: InjectableCss;
    compareCheckbox?: CheckboxPresentationProps;

    // shared
    priceStlye?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    unitOfMeasureContainer?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    addToCartContainer?: InjectableCss;
    qtyGridItem?: InjectableCss;
    productQuantityOrderedStyles?: ProductQtyOrderedStyles;
    addToCartButtonGridItem?: InjectableCss;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    addToCartButtonListProps?: any;
    addToCartButtonGridProps?: any;
}

export const actionsStyles: ProductListActionsStyles = {
    listActionsWrapper: {
        css: css`
            padding-top: 11px;
            padding-bottom: 11px;
        `,
    },
    listPriceWrapper: {
        css: css`
            min-height: 22px;
            margin-bottom: 18px;
            display: flex;
            justify-content: flex-end;
            span {
                white-space: pre-wrap !important;
            }
        `,
    },
    listCompareListWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: 16px;

            > div {
                &:first-child {
                    padding-right: 5px;
                }
                &:last-child {
                    padding-left: 5px;
                }
            }
        `,
    },
    priceStlye: {
        wrapper: {
            css: css`
                width: auto;
            `,
        },
        price: {
            priceText: {
                size: "18px",
                lineHeight: "22px",
            },
            nonStockText: {
                css: css`
                    ${productPriceStyles.price?.nonStockText?.css}
                    max-width: 235px;
                `,
            },
        },
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-bottom: 10px;
            `,
        },
    },
    addToCartContainer: {
        css: css`
            display: flex;
        `,
    },
    productQuantityOrderedStyles: {
        inputStyles: {
            cssOverrides: {
                formField: css`
                    margin-top: 0;
                `,
            },
        },
    },
    qtyGridItem: {
        css: css`
            padding-right: 10px;
            flex-grow: 1;
        `,
    },
    addToCartButtonGridItem: {
        css: css`
            .icon-only {
                width: 85px !important;
            }
        `,
    },
    gridActionsWrapper: {
        css: css`
            padding-top: 10px;
            display: flex;
            flex-direction: column;
            height: 100%;
        `,
    },
    gridPriceAvailablityWrapper: {
        css: css`
            margin-bottom: 10px;
            display: flex;
            flex-direction: column;
            flex-grow: 1;

            > div {
                &:first-child {
                    margin-bottom: 5px;
                }
            }
        `,
    },
    productAvailabilityStyles: {
        nonStockText: {
            css: css`
                ${productAvailabilityStyles.nonStockText?.css}
                max-width: 235px;
            `,
        },
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    showAddToList,
    showCompare,
    view,
    productCompareChecked,
    addProductToCompare,
    removeProductFromCompare,
    isProductCompareFull,
    showPrice,
    showAvailability,
}) => {
    const toasterContext = React.useContext(ToasterContext);

    const { product } = productContext;

    if (!product) {
        return null;
    }

    const handleChanged = (_: React.SyntheticEvent, value: boolean) => {
        if (value && isProductCompareFull) {
            toasterContext.addToast({
                body: siteMessage("ProductionCompare_MaximumItemsReached"),
                messageType: "warning",
            });
        } else if (value) {
            addProductToCompare({ productId: productContext.product.id });
        } else {
            removeProductFromCompare({ productId: productContext.product.id });
        }
    };

    return (
        <>
            {view !== "List" && (
                <StyledWrapper {...styles.gridActionsWrapper} className="grid-actions-wrapper">
                    <StyledWrapper {...styles.gridPriceAvailablityWrapper}>
                        <StyledWrapper {...styles.gridPriceWrapper}>
                            {showPrice && (
                                <StyledWrapper {...styles.productPriceWrapper}>
                                    <VisuallyHidden
                                        as="label"
                                        htmlFor={`${product.id}-uom`}
                                        id={`${product.id}-uom-label`}
                                    >
                                        {translate("U/M")}
                                    </VisuallyHidden>
                                    <ProductPrice
                                        showUnitOfMeasure={true}
                                        showLabel={false}
                                        product={productContext}
                                        extendedStyles={styles.priceStlye as any}
                                    />
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.gridInventoryWrapper}>
                            {productSettings.showInventoryAvailability && showAvailability && (
                                <ProductContextAvailability
                                    stockOnly={true}
                                    extendedStyles={styles.productAvailabilityStyles as any}
                                    {...({ isProductListPage: true } as any)}
                                />
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.gridActionsContentWrapper}>
                        {!productContext.product.isVariantParent ? (
                            <>
                                <StyledWrapper {...styles.unitOfMeasureContainer}>
                                    {product.unitOfMeasures && (
                                        <ProductUnitOfMeasureSelect
                                            labelOverride=""
                                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                            id={`${product.id}-uom`}
                                        />
                                    )}
                                </StyledWrapper>
                                <StyledWrapper {...styles.addToCartContainer}>
                                    <StyledWrapper {...styles.qtyGridItem} className="grid-qty">
                                        <VisuallyHidden
                                            as="label"
                                            htmlFor={`${product.id}-qty`}
                                            id={`${product.id}-qty-label`}
                                        >
                                            {translate("QTY")}
                                        </VisuallyHidden>
                                        <ProductQuantityOrdered
                                            labelOverride=""
                                            {...({ extendedStyles: styles.productQuantityOrderedStyles } as any)}
                                            id={`${product.id}-qty`}
                                        />
                                    </StyledWrapper>

                                    <StyledWrapper {...styles.addToCartButtonGridItem}>
                                        <ProductAddToCartButton
                                            data-test-selector="addToCartBtn"
                                            extendedStyles={styles.productAddToCartButtonStyles}
                                            {...styles.addToCartButtonGridProps}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            </>
                        ) : (
                            <Button href={product.uri}>{translate("More Details")}</Button>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {view === "List" && (
                <StyledWrapper {...styles.listActionsWrapper} className="list-actions-wrapper">
                    <StyledWrapper {...styles.listPriceWrapper}>
                        {showPrice && (
                            <StyledWrapper {...styles.productPriceWrapper}>
                                <VisuallyHidden as="label" htmlFor={`${product.id}-uom`} id={`${product.id}-uom-label`}>
                                    {translate("U/M")}
                                </VisuallyHidden>
                                <ProductPrice
                                    showUnitOfMeasure={true}
                                    showLabel={false}
                                    product={productContext}
                                    extendedStyles={{
                                        ...((styles.priceStlye as any) || {}),
                                        price: {
                                            ...(productPriceStyles.price || {}),
                                            ...((styles.priceStlye?.price as any) || {}),
                                            nonStockText: {
                                                css: css`
                                                    ${productPriceStyles.price?.nonStockText?.css}
                                                    max-width: 280px;
                                                `,
                                            },
                                        } as any,
                                    }}
                                />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.listActionsContentWrapper}>
                        {!productContext.product.isVariantParent ? (
                            <>
                                <StyledWrapper {...styles.unitOfMeasureContainer}>
                                    {product.unitOfMeasures && (
                                        <ProductUnitOfMeasureSelect
                                            labelOverride=""
                                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                            id={`${product.id}-uom`}
                                        />
                                    )}
                                </StyledWrapper>
                                <StyledWrapper {...styles.addToCartContainer}>
                                    <StyledWrapper {...styles.qtyGridItem}>
                                        <VisuallyHidden
                                            as="label"
                                            htmlFor={`${product.id}-qty`}
                                            id={`${product.id}-qty-label`}
                                        >
                                            {translate("QTY")}
                                        </VisuallyHidden>
                                        <ProductQuantityOrdered
                                            labelOverride=""
                                            {...({ extendedStyles: styles.productQuantityOrderedStyles } as any)}
                                            id={`${product.id}-qty`}
                                        />
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.addToCartButtonGridItem} className="list-atc">
                                        <ProductAddToCartButton
                                            data-test-selector="addToCartBtn"
                                            extendedStyles={styles.productAddToCartButtonStyles}
                                            {...styles.addToCartButtonListProps}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            </>
                        ) : (
                            <Button href={product.uri}>{translate("More Details")}</Button>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.listCompareListWrapper}>
                        <StyledWrapper {...styles.listAddToListWrapper}>
                            {showAddToList && (
                                <ProductAddToListLink
                                    data-test-selector={`list_actionsAddToList${productContext.product.id}`}
                                />
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.listCompareWrapper}>
                            {showCompare && productSettings.enableProductComparisons && (
                                <Checkbox
                                    {...styles.compareCheckbox}
                                    checked={productCompareChecked}
                                    onChange={handleChanged}
                                    data-test-selector="productList_CompareCheckbox"
                                >
                                    {translate("Compare")}
                                </Checkbox>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductListActions));
